import { groupBy, pickBy } from "lodash-es";
import {
  acceptHotItemStore,
  defineItemStore
} from "@editor/items/itemStore.store";

import type { EffectActivatorType, EffectItem } from "./EffectItem";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";

export const effectItemStructure: EffectItem = {
  type: "effect",
  id: -1,
  uid: "effect_0",

  affectedType: "target", // target or resource
  affectedId: -1,
  activatorType: "incident", // incident or action
  activatorId: null,
  impulseExpressions: [""],

  hint: "",
  visibility: "visible",
  pendingEffectsVisible: true,
  pendingEffectsOpenable: true,

  docs: {},
  order: -1,
  createdAt: -1
};

export const useEffectStore = defineItemStore("model/effects", effectItemStructure, {
  getters: {
    getByActivator() {
      return (activator: { type: string; id: number }) => {
        const effects = this.orderedItems.filter(
          (effect) =>
            effect.activatorType === activator.type &&
            effect.activatorId === activator.id
        );
        effects.sort((a, b) => {
          // resources first
          if (a.affectedType == "resource") {
            return -1;
          } else if (b.affectedType == "resource") {
            return 1;
          } else {
            if (a.affectedType == "target") {
              const store = useTargetStore();
              const orderA = store.order.indexOf(a.affectedId);
              const orderB = store.order.indexOf(b.affectedId);
              return orderA - orderB;
            } else {
              const store = useResourceStore();
              const orderA = store.order.indexOf(a.affectedId);
              const orderB = store.order.indexOf(b.affectedId);
              return orderA - orderB;
            }
          }
        });
        return effects;
      };
    },
    byAction(): { [actionId: number]: EffectItem[] } {
      const allEffects = this.orderedItems;
      const filteredItems = allEffects.filter(
        (effect) => effect.activatorType === "action"
      );
      return groupBy(filteredItems, "activatorId");
    },
    byIncident(): { [incidentId: number]: EffectItem[] } {
      const allEffects = this.orderedItems;
      const incidentEffects = allEffects.filter(
        (effect) => effect.activatorType === "incident"
      );
      return groupBy(incidentEffects, "activatorId");
    },
    byResource(): { [resourceId: number]: EffectItem[] } {
      const allEffects = this.orderedItems;
      const resourceEffects = allEffects.filter(
        (effect) => effect.affectedType === "resource"
      );
      return groupBy(resourceEffects, "affectedId");
    },
    byTarget(): { [targetId: number]: EffectItem[] } {
      const allEffects = this.orderedItems;
      const targetEffects = allEffects.filter(
        (effect) => effect.affectedType === "target"
      );
      return groupBy(targetEffects, "affectedId");
    },
    byAffectedUid(): { [uid: string]: EffectItem[] } {
      const allEffects = this.orderedItems;
      return groupBy(allEffects, (effect) => `${effect.affectedType}_${effect.affectedId}`);
    },
    findOn() {
      return (item: { id: number; type: string }) => {
        const affectedType = item.type;
        const affectedId = item.id;
        return pickBy(
          this.byId,
          (effect) =>
            effect.affectedType === affectedType &&
            effect.affectedId === affectedId
        );
      };
    }
  },
  actions: {
    connectActivatorEffects(
      activator: { type: EffectActivatorType; id: number },
      items: { type: "target" | "resource"; id: number }[]
    ) {
      const effectsToCreate = [];
      const effects = this.getByActivator(activator);

      // create effects for elements that are not yet connected
      for (const item of items) {
        const index = effects.findIndex(
          (effect) =>
            effect.affectedType === item.type && effect.affectedId === item.id
        );
        if (index === -1) {
          effectsToCreate.push({
            affectedId: item.id,
            affectedType: item.type,
            activatorId: activator.id,
            activatorType: activator.type
          });
        } else {
          // remove the effect from the list if it exists already
          effects.splice(index, 1);
        }
      }

      // delete all effects that were not chosen again
      this.deleteMany(effects);

      // create all new effects
      this.createMany(effectsToCreate);
    }
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHotItemStore(useEffectStore, import.meta.hot));
}
